"use client";

import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { XIcon, DownloadSimpleIcon, FileCloudIcon, FunnelIcon, ListPlusIcon, NewspaperClippingIcon, CheckIcon, CaretDoubleDownIcon, CaretDoubleUpIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Tooltip } from "@heroui/tooltip";
import MediaDisplay from "./ui/mediaDisplay";
import { MediaObject, MediaType, SourceContext, SourceInfo, Source, SourceObject } from "@/shared/types";
import { getMediaType, extractPathAfterBulk, handleDownload as handleDownloadSource, isRestrictedFileType } from "@/shared/functions";
import { UsableMediaTypes } from "@/shared/constants";
import type { EventFromChat } from "./answer-item";
import { useOptionalToolsSharedCaseData } from "./tools-shared-case-data";
import { FeaturesContext } from "@/app/contexts";
dayjs.extend(relativeTime);
const MEDIA_SHARE_STORAGE_KEY_PREFIX = "tpl-media-source-share";
const getStorageKey = (caseId: string | undefined) => `${MEDIA_SHARE_STORAGE_KEY_PREFIX}-${caseId || "no-case"}`;
const MEDIA_SHARE_MAX_ENTRIES = 50;
const PREVIEW_MAX_CHARS = 80;
export interface MediaShareEntry {
  id: string;
  mediaType: MediaType;
  mediaUrl?: string;
  mediaSrc?: string;
  mediaContext?: SourceContext;
  sourceInfo?: SourceInfo | null;
  truncatedFilePath?: string;
  content?: string;
  page?: string;
  location?: string;
  timestamp: number;
}

/**
 * Resolved PDF/page index for share entries — no default to 1 (avoids false "page 1" for AV / no-page sources).
 * Aligns with UnifiedSources source identity keys (`pageNumber > 0` only).
 */
export function resolvedShareEntryPage(entry: {
  page?: string;
  mediaContext?: SourceContext;
}): number | undefined {
  const ctx = entry.mediaContext ?? {};
  const fromEntry = entry.page != null && String(entry.page).trim() !== "" ? Number.parseInt(String(entry.page), 10) : NaN;
  if (typeof ctx.pageNumber === "number" && Number.isFinite(ctx.pageNumber) && ctx.pageNumber > 0) {
    return ctx.pageNumber;
  }
  if (typeof ctx.page === "number" && Number.isFinite(ctx.page) && ctx.page > 0) {
    return ctx.page;
  }
  if (Number.isFinite(fromEntry) && fromEntry > 0) {
    return fromEntry;
  }
  return undefined;
}

/** Build a {@link SourceObject} for UnifiedSources / event modal (same shape as search results). */
export function mediaShareEntryToSourceObject(entry: MediaShareEntry): SourceObject | null {
  const url = (entry.mediaUrl || entry.mediaSrc || "").trim();
  if (!url) return null;
  const pageNum = resolvedShareEntryPage(entry);
  const nextCtx: SourceContext = {
    ...(entry.mediaContext ?? {})
  };
  if (pageNum !== undefined) {
    nextCtx.pageNumber = pageNum;
    nextCtx.page = pageNum;
  } else {
    delete nextCtx.pageNumber;
    delete nextCtx.page;
  }
  return {
    sourceType: entry.mediaType,
    sourceUrl: url,
    truncatedFilePath: entry.truncatedFilePath,
    sourceContext: nextCtx,
    sourceInfo: entry.sourceInfo ?? undefined
  };
}
function loadMediaShareEntries(storageKey: string): MediaShareEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MediaShareEntry[];
    return Array.isArray(parsed) ? parsed.slice(0, MEDIA_SHARE_MAX_ENTRIES) : [];
  } catch {
    return [];
  }
}
export type MediaSourceShareContextValue = {
  addToMediaShare: (item: MediaObject | Source | Record<string, unknown>, onSuccess?: () => void) => void;
  toggleDrawer: () => void;
  isDrawerOpen: boolean;
  /** State for Tools Drawer to render MediaSourceShareContent (only set when provider is mounted). */
  entries: MediaShareEntry[];
  setEntries: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  persistEntries: (next: MediaShareEntry[]) => void;
  /** Reload entries from server (shared case) or localStorage. */
  refetchEntries: () => void;
  storageKey: string;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
};
const MediaSourceShareContext = createContext<MediaSourceShareContextValue | null>(null);
export function useMediaSourceShare() {
  return useContext(MediaSourceShareContext);
}
function sourceOrObjectToMediaObject(item: MediaObject | Source | Record<string, unknown>): MediaObject | null {
  const src = item as Record<string, unknown>;
  const sourceUrl = src.sourceUrl as string || src.mediaUrl as string || src.mediaSrc as string || src.sourceFilePath as string || src.previewFilePath as string || src.derived_object_url as string;
  if (!sourceUrl) return null;
  const mediaType = getMediaType({
    mediaType: "unknown",
    mediaUrl: sourceUrl
  } as MediaObject, src.sourceInfo as SourceInfo | null) as MediaType;
  if (!UsableMediaTypes.includes(mediaType)) return null;
  const sourcePage = (src.sourceContext as Record<string, unknown>)?.pageNumber ?? src.pageNumber ?? src.page ?? 1;
  const pageNum = typeof sourcePage === "string" ? parseInt(String(sourcePage), 10) : Number(sourcePage);
  return {
    mediaType,
    mediaUrl: sourceUrl,
    mediaSrc: sourceUrl,
    mediaContext: {
      ...(src.sourceContext as SourceContext | undefined),
      pageNumber: pageNum,
      page: pageNum,
      previewUrl: src.previewFilePath as string | undefined,
      transcriptUrl: src.location as string | undefined,
      startTime: (src.sourceContext as Record<string, unknown>)?.startTime as number | undefined
    },
    sourceInfo: src.sourceInfo as SourceInfo | null | undefined,
    previewFilePath: src.previewFilePath as string | undefined
  };
}

/** Optional drawer API injected by Tools Drawer when mounted (avoids circular dependency). */
export type InjectedDrawerApi = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  openDrawerToTab: (tab: "sources" | "recently-copied" | "research-notes" | "captures") => void;
};
export function MediaSourceShareProvider({
  children,
  caseId,
  onAddToTimeline,
  injectDrawer
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
  injectDrawer?: InjectedDrawerApi | null;
}) {
  const storageKey = getStorageKey(caseId);
  const remote = useOptionalToolsSharedCaseData();
  const useRemoteShare = Boolean(caseId && remote && remote.caseId === caseId);
  const [localEntries, setLocalEntries] = useState<MediaShareEntry[]>(() => useRemoteShare ? [] : loadMediaShareEntries(storageKey));
  const [localOpen, setLocalOpen] = useState(false);
  useEffect(() => {
    if (!useRemoteShare) {
      setLocalEntries(loadMediaShareEntries(storageKey));
    }
  }, [storageKey, useRemoteShare]);
  const entries = useRemoteShare ? remote!.hydrated ? remote!.sources : [] : localEntries;
  const setEntries = useRemoteShare ? remote!.setSources : setLocalEntries;
  const persistEntries = useCallback((next: MediaShareEntry[]) => {
    if (useRemoteShare) {
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [useRemoteShare, storageKey]);
  const remoteRefetch = remote?.refetch;
  const refetchEntries = useCallback(() => {
    if (useRemoteShare) {
      if (remoteRefetch) void remoteRefetch();
      return;
    }
    setLocalEntries(loadMediaShareEntries(storageKey));
  }, [useRemoteShare, remoteRefetch, storageKey]);
  const isDrawerOpen = injectDrawer ? injectDrawer.isOpen : localOpen;
  const toggleDrawer = useCallback(() => {
    if (injectDrawer) {
      if (injectDrawer.isOpen) {
        injectDrawer.closeDrawer();
      } else {
        injectDrawer.openDrawerToTab("sources");
      }
    } else {
      setLocalOpen(prev => !prev);
    }
  }, [injectDrawer]);
  const addToMediaShare = useCallback((item: MediaObject | Source | Record<string, unknown>, onSuccess?: () => void) => {
    const mediaObj = (item as MediaObject).mediaType && (item as MediaObject).mediaUrl ? item as MediaObject : sourceOrObjectToMediaObject(item);
    if (!mediaObj || !UsableMediaTypes.includes(mediaObj.mediaType)) return;
    const url = mediaObj.mediaUrl || mediaObj.mediaSrc || "";
    const pathForLabel = (item as Record<string, unknown>).truncatedFilePath as string || url || "Unknown";
    const truncated = extractPathAfterBulk(pathForLabel, true).truncatedFilePath;
    const raw = item as Record<string, unknown>;
    const content = raw.content as string ?? undefined;
    const page = raw.page as string ?? undefined;
    const location = raw.location as string ?? undefined;
    setEntries(prev => {
      const now = Date.now();
      const contentNorm = (content ?? "").trim();
      const newPage = resolvedShareEntryPage({
        page,
        mediaContext: mediaObj.mediaContext
      });
      const existing = prev.find(e => (e.mediaUrl === url || e.mediaSrc === url) && resolvedShareEntryPage(e) === newPage && (e.content ?? "").trim() === contentNorm);
      let next: MediaShareEntry[];
      if (existing) {
        next = [{
          ...existing,
          content: content ?? existing.content,
          page: page ?? existing.page,
          location: location ?? existing.location,
          timestamp: now
        }, ...prev.filter(e => e.id !== existing.id)];
      } else {
        next = [{
          id: crypto.randomUUID(),
          mediaType: mediaObj.mediaType,
          mediaUrl: mediaObj.mediaUrl,
          mediaSrc: mediaObj.mediaSrc,
          mediaContext: mediaObj.mediaContext,
          sourceInfo: mediaObj.sourceInfo,
          truncatedFilePath: truncated,
          content,
          page,
          location,
          timestamp: now
        }, ...prev].slice(0, MEDIA_SHARE_MAX_ENTRIES);
      }
      persistEntries(next);
      return next;
    });
    onSuccess?.();
  }, [persistEntries, setEntries]);
  const contextValue = useMemo((): MediaSourceShareContextValue => ({
    addToMediaShare,
    toggleDrawer,
    isDrawerOpen,
    entries,
    setEntries,
    persistEntries,
    refetchEntries,
    storageKey,
    caseId,
    onAddToTimeline
  }), [addToMediaShare, toggleDrawer, isDrawerOpen, entries, setEntries, persistEntries, refetchEntries, storageKey, caseId, onAddToTimeline]);
  return <MediaSourceShareContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="MediaSourceShareProvider" data-sentry-source-file="mediaSourceShare.tsx">
      {children}
    </MediaSourceShareContext.Provider>;
}
export function MediaSourceShareContent({
  entries,
  setEntries,
  persistEntries,
  storageKey,
  caseId,
  onAddToTimeline,
  addSharedSourceToEvent,
  eventSharedSourceAddReady = false
}: {
  entries: MediaShareEntry[];
  setEntries: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  persistEntries: (next: MediaShareEntry[]) => void;
  storageKey: string;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
  /** From tools drawer: append a source to the open event modal's selected-sources list. */
  addSharedSourceToEvent?: (source: SourceObject) => boolean;
  eventSharedSourceAddReady?: boolean;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [justAddedToEventSources, setJustAddedToEventSources] = useState(false);
  const [addedToScopeId, setAddedToScopeId] = useState<string | null>(null);
  const [copiedPathId, setCopiedPathId] = useState<string | null>(null);
  const [createdEventId, setCreatedEventId] = useState<string | null>(null);
  const [isCitationExpanded, setIsCitationExpanded] = useState(true);
  const entryToSource = useCallback((entry: MediaShareEntry): Source => ({
    sourceFilePath: entry.mediaUrl || entry.mediaSrc,
    sourceUrl: entry.mediaUrl || entry.mediaSrc,
    truncatedFilePath: entry.truncatedFilePath,
    sourceContext: entry.mediaContext,
    sourceInfo: entry.sourceInfo,
    page: entry.page ?? entry.mediaContext?.pageNumber?.toString(),
    content: entry.content,
    location: entry.location
  }), []);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const handleDownload = useCallback((entry: MediaShareEntry) => {
    handleDownloadSource(entryToSource(entry), !!featureFlags?.useIntercom);
  }, [entryToSource, featureFlags?.useIntercom]);
  const handleAddToScope = useCallback((entry: MediaShareEntry) => {
    const path = entry.mediaUrl || entry.mediaSrc || entry.truncatedFilePath || "";
    if (!path || !caseId) return;
    const key = `${caseId}-scopesObject`;
    const current = JSON.parse(sessionStorage.getItem(key) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }');
    current.files = Array.from(new Set([...current.files, path]));
    sessionStorage.setItem(key, JSON.stringify(current));
    window.dispatchEvent(new CustomEvent("scopes-updated", {
      detail: {
        caseId
      }
    }));
    setAddedToScopeId(entry.id);
    setTimeout(() => setAddedToScopeId(null), 1500);
  }, [caseId]);
  const handleAddSelectedEntryToEventSources = useCallback(() => {
    if (!addSharedSourceToEvent || !eventSharedSourceAddReady) return;
    const entry = entries.find(e => e.id === selectedId) ?? (entries.length > 0 ? entries[0] : undefined);
    if (!entry) return;
    const source = mediaShareEntryToSourceObject(entry);
    if (!source) return;
    const ok = addSharedSourceToEvent(source);
    if (!ok) return;
    setJustAddedToEventSources(true);
    setTimeout(() => setJustAddedToEventSources(false), 1500);
  }, [addSharedSourceToEvent, eventSharedSourceAddReady, entries, selectedId]);
  const handleCopyPath = useCallback(async (entry: MediaShareEntry) => {
    const path = entry.truncatedFilePath || entry.mediaUrl || entry.mediaSrc || "";
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPathId(entry.id);
      setTimeout(() => setCopiedPathId(null), 1500);
    } catch {
      /* ignore */
    }
  }, []);
  const handleCreateEvent = useCallback((entry: MediaShareEntry) => {
    if (!onAddToTimeline || !caseId) return;
    const source = entryToSource(entry);
    const mediaObj = entry as MediaObject;
    const event: EventFromChat = {
      answerText: "FROM_SOURCE",
      sources: [source],
      media: [mediaObj],
      autogen: false,
      epochTimeSecs: Math.floor(Date.now() / 1000)
    };
    onAddToTimeline(event);
    setCreatedEventId(entry.id);
    setTimeout(() => setCreatedEventId(null), 1500);
  }, [onAddToTimeline, caseId, entryToSource]);
  useEffect(() => {
    setSelectedId(null);
  }, [storageKey]);
  useEffect(() => {
    setIsCitationExpanded(true);
  }, [selectedId]);
  const remoteShare = useOptionalToolsSharedCaseData();
  const skipStorageSync = Boolean(caseId && remoteShare && remoteShare.caseId === caseId);
  useEffect(() => {
    if (skipStorageSync) return;
    const handler = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue != null) {
        try {
          const parsed = JSON.parse(event.newValue) as MediaShareEntry[];
          if (Array.isArray(parsed)) {
            setEntries(parsed.slice(0, MEDIA_SHARE_MAX_ENTRIES));
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, setEntries, skipStorageSync]);
  const handleRemove = useCallback((entryId: string) => {
    setEntries(prev => {
      const next = prev.filter(e => e.id !== entryId);
      persistEntries(next);
      return next;
    });
    setSelectedId(prev => prev === entryId ? null : prev);
  }, [persistEntries, setEntries]);
  const selectedEntry = entries.find(e => e.id === selectedId) ?? entries[0];
  const labelForEntry = (e: MediaShareEntry) => {
    const p = e.truncatedFilePath;
    if (p && p.trim()) return p;
    const u = e.mediaUrl || e.mediaSrc || "";
    if (!u) return "Unknown";
    const parts = u.split("/");
    return parts[parts.length - 1] || u;
  };
  const previewLabel = (label: string) => {
    const t = label.trim();
    if (t.length <= PREVIEW_MAX_CHARS) return t;
    return t.slice(0, PREVIEW_MAX_CHARS) + "…";
  };
  return <div className="flex flex-1 min-h-0 flex-row overflow-hidden" data-sentry-component="MediaSourceShareContent" data-sentry-source-file="mediaSourceShare.tsx">
        <div className={`flex ${entries.length > 0 ? "w-[280px]" : "w-full"} shrink-0 flex-col ${entries.length > 0 ? "border-r border-white/20" : ""} overflow-hidden`}>
            <div className="shrink-0 px-3 py-2 border-b border-white/20">
              <span className="text-white/80 font-light text-[13px] tracking-wider">
                Added items:
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {entries.length === 0 ? <p className="px-3 py-4 text-white/50 text-xs">
                  Use &quot;Add to Favorites&quot; next to a source to add it
                  here.
                </p> : <ul className="flex flex-col gap-0 list-none pl-0">
                  {entries.map(entry => <li key={entry.id} className={`flex items-start gap-2 border-b border-white/10 px-3 py-2 cursor-pointer ${selectedId === entry.id ? "bg-white/10" : "hover:bg-white/5"}`} onClick={() => setSelectedId(entry.id)}>
                      <div className="min-w-0 flex-1">
                        <p className="text-white/90 text-xs line-clamp-2 break-words" title={labelForEntry(entry)}>
                          {previewLabel(labelForEntry(entry))}
                        </p>
                        <p className="text-white/50 text-[11px] mt-1">
                          {dayjs(entry.timestamp).fromNow()}
                        </p>
                      </div>
                      <button type="button" onClick={e => {
              e.stopPropagation();
              handleRemove(entry.id);
            }} className="rounded p-1 shrink-0 cursor-pointer text-white/60 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30" aria-label="Remove from list">
                        <XIcon size={16} weight="bold" />
                      </button>
                    </li>)}
                </ul>}
            </div>
        </div>
          {entries.length > 0 && <div className="flex flex-1 min-w-0 min-h-0 flex-col p-3 overflow-hidden">
            {selectedEntry ? <>
                <div className="flex flex-row gap-1 shrink-0 pb-2 border-b border-white/20 mb-2">
                  <Tooltip content="Download source" placement="bottom" delay={0} classNames={{
            content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
            base: "before:bg-tooltip-bg"
          }}>
                    <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${isRestrictedFileType(selectedEntry) ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={() => handleDownload(selectedEntry)} disabled={isRestrictedFileType(selectedEntry)} aria-label="Download source">
                      <DownloadSimpleIcon size={20} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Add source to scope" placement="bottom" delay={0} classNames={{
            content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
            base: "before:bg-tooltip-bg"
          }}>
                    <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${addedToScopeId === selectedEntry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={() => handleAddToScope(selectedEntry)} disabled={!caseId} aria-label="Add source to scope">
                      {addedToScopeId === selectedEntry.id ? <CheckIcon size={20} /> : <FunnelIcon size={20} />}
                    </button>
                  </Tooltip>
                  <Tooltip content={copiedPathId === selectedEntry.id ? "Copied" : "Copy source file path"} placement="bottom" delay={0} classNames={{
            content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
            base: "before:bg-tooltip-bg"
          }}>
                    <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${copiedPathId === selectedEntry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={() => handleCopyPath(selectedEntry)} aria-label={copiedPathId === selectedEntry.id ? "Copied" : "Copy source file path"}>
                      {copiedPathId === selectedEntry.id ? <CheckIcon size={20} /> : <FileCloudIcon size={20} />}
                    </button>
                  </Tooltip>
                  <Tooltip content={createdEventId === selectedEntry.id ? "Event created" : "Create timeline event from source"} placement="bottom" delay={0} classNames={{
            content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
            base: "before:bg-tooltip-bg"
          }}>
                    <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${createdEventId === selectedEntry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={() => handleCreateEvent(selectedEntry)} disabled={!onAddToTimeline || !caseId} aria-label={createdEventId === selectedEntry.id ? "Event created" : "Create timeline event from source"}>
                      {createdEventId === selectedEntry.id ? <CheckIcon size={20} /> : <ListPlusIcon size={20} />}
                    </button>
                  </Tooltip>
                  <Tooltip content={eventSharedSourceAddReady ? "Add this source to Selected Sources in the open event (or title slide) editor" : "Open an event or title slide for editing to add this source"} placement="bottom" delay={0} classNames={{
            content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[260px]",
            base: "before:bg-tooltip-bg"
          }}>
                    <button type="button" className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${justAddedToEventSources ? "bg-brand-blue text-white" : eventSharedSourceAddReady ? "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" : "text-white/20 cursor-not-allowed"}`} onClick={handleAddSelectedEntryToEventSources} disabled={!eventSharedSourceAddReady} aria-label="Add source to event selected sources">
                      {justAddedToEventSources ? <CheckIcon size={20} /> : <NewspaperClippingIcon size={20} weight="duotone" />}
                    </button>
                  </Tooltip>
                </div>
                {(selectedEntry.page && !isNaN(parseInt(selectedEntry.page)) || selectedEntry.content && selectedEntry.content.trim() !== "" && !selectedEntry.location?.includes("/bkb/slow/transcribe/") && !selectedEntry.location?.endsWith(".transcript.txt")) && <div className="flex flex-row items-top justify-start w-full shrink-0 pb-2 border-b border-white/20 mb-2">
                    {selectedEntry.content && selectedEntry.content.trim() !== "" && !selectedEntry.location?.includes("/bkb/slow/transcribe/") && !selectedEntry.location?.endsWith(".transcript.txt") && <div className="cursor-pointer w-[20px] h-[20px] pl-1 pr-2" onClick={() => setIsCitationExpanded(p => !p)}>
                        {isCitationExpanded ? <CaretDoubleUpIcon size={20} /> : <CaretDoubleDownIcon size={20} />}
                      </div>}
                    <div className={`box-border flex flex-col gap-0 flex-1 min-w-0 ${selectedEntry.content && selectedEntry.content.trim() !== "" && !selectedEntry.location?.includes("/bkb/slow/transcribe/") && !selectedEntry.location?.endsWith(".transcript.txt") && !isCitationExpanded ? "h-[21px] max-h-[21px] overflow-hidden opacity-0 transition-opacity duration-300 ease-in-out" : ""}`}>
                      {selectedEntry.page && !isNaN(parseInt(selectedEntry.page)) && <div className="text-white font-normal text-base mb-2">
                          Page {selectedEntry.page}
                        </div>}
                      {selectedEntry.content && selectedEntry.content.trim() !== "" && !selectedEntry.location?.includes("/bkb/slow/transcribe/") && !selectedEntry.location?.endsWith(".transcript.txt") && <div className="flex flex-col gap-2">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                p: ({
                  node,
                  children
                }) => <span className="mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal">{children}</span>,
                h2: ({
                  node,
                  children
                }) => <span className="mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal">{children}</span>,
                li: ({
                  node,
                  children
                }) => <li className="mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal">{children}</li>,
                a: ({
                  node,
                  children
                }) => <a className="text-brand-blue-light italic">{children}</a>
              }}>
                            {selectedEntry.content?.replace("```json ", "")}
                          </ReactMarkdown>
                        </div>}
                    </div>
                  </div>}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar2">
                  <MediaDisplay mediaItem={{
            ...selectedEntry,
            mediaContext: {
              ...selectedEntry.mediaContext,
              htmlChunk: selectedEntry.content ? selectedEntry.content.match(/page \d+/i)?.[0] || "" : "",
              pageNumber: selectedEntry.page ? parseInt(selectedEntry.page, 10) : selectedEntry.mediaContext?.pageNumber ?? 1
            }
          } as MediaObject} sourcesExpanded={false} isMediaShareDrawer={true} />
                </div>
              </> : <div className="flex flex-1 items-center justify-center text-white/50 text-sm">
                Select an item to view
              </div>}
          </div>}
    </div>;
}
export default MediaSourceShareProvider;